import * as React from 'react';

import {
  enumQuoteStatus,
  enumQuoteType,
  QuoteStatus,
  QuoteType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  SearchField,
  Toggle,
  ToggleButtonGroup,
} from '@rematter/ui-web';
import { useAtom, useSetAtom } from 'jotai';
import { LucideX } from 'lucide-react';

import * as state from '../../_state/quotesList.state';
import { useTranslations } from '../../i18n/useTranslations';

import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

//
// ⚛️ ⚛️ ⚛️ QUOTE LIST FILTERS ⚛️ ⚛️ ⚛️
//
export const QuoteListFilters = () => {
  const t = useTranslations();

  const [selectedQuoteType, setSelectedQuoteType] = useAtom(
    state.$selectedQuoteType,
  );
  const [selectedStatuses, setSelectedStatuses] = useAtom(
    state.$selectedStatuses,
  );
  const [selectedFacilityId, setSelectedFacilityId] = useAtom(
    state.$selectedFacilityId,
  );
  const [selectedAccountIds, setSelectedAccountIds] = useAtom(
    state.$selectedAccountIds,
  );
  const setOffset = useSetAtom(state.$offset);

  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 300);

  const selectedStatusValue = React.useMemo(() => {
    const arr = Array.from(selectedStatuses);
    return arr.length === 1 ? (arr[0] as QuoteStatus) : undefined;
  }, [selectedStatuses]);

  const selectedAccountId = React.useMemo(() => {
    const arr = Array.from(selectedAccountIds);
    return arr.length === 1 ? arr[0] : undefined;
  }, [selectedAccountIds]);

  const hasActiveFilters = React.useMemo(
    () =>
      (searchTerm !== undefined && searchTerm?.trim() !== '') ||
      selectedStatuses.size > 0 ||
      !!selectedFacilityId ||
      selectedAccountIds.size > 0,
    [searchTerm, selectedStatuses, selectedFacilityId, selectedAccountIds],
  );

  const handleClearAll = React.useCallback(() => {
    setSearchTerm('');
    setSelectedStatuses(new Set());
    setSelectedFacilityId(undefined);
    setSelectedAccountIds(new Set());
    setOffset(0);
  }, [
    setSearchTerm,
    setSelectedStatuses,
    setSelectedFacilityId,
    setSelectedAccountIds,
    setOffset,
  ]);

  return (
    <>
      <ToggleButtonGroup
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={[selectedQuoteType]}
        onSelectionChange={(keys) => {
          const value = Array.from(keys)[0] as QuoteType;
          setSelectedQuoteType(value);
          setOffset(0);
        }}
      >
        <Toggle id={enumQuoteType.Sales}>{t.quotes.types.salesShort}</Toggle>
        <Toggle id={enumQuoteType.Purchase}>
          {t.quotes.types.purchaseShort}
        </Toggle>
      </ToggleButtonGroup>
      <SearchField
        autoFocus
        value={searchTerm ?? ''}
        onChange={setSearchTerm}
        placeholder={t.quotes.list.search}
        aria-label={t.quotes.list.ariaLabel}
        className="w-[200px] shrink-0"
      />
      <SelectFacility
        value={selectedFacilityId ?? undefined}
        onChange={(value) => {
          setSelectedFacilityId(value ?? undefined);
          setOffset(0);
        }}
        showAllOption
        allOptionLabel={t.quotes.list.filters.allFacilities}
        className="w-[180px]"
        aria-label={t.quotes.list.filters.allFacilities}
      />
      <SelectAccount
        value={selectedAccountId}
        onChange={(value) => {
          setSelectedAccountIds(value ? new Set([value]) : new Set());
          setOffset(0);
        }}
        className="w-[200px]"
        aria-label="Account"
      />
      <SelectEnum<QuoteStatus>
        enumValue={enumQuoteStatus}
        value={selectedStatusValue}
        onChange={(value) => {
          setSelectedStatuses(value ? new Set([value]) : new Set());
          setOffset(0);
        }}
        showAllOption
        allOptionLabel={t.quotes.list.filters.allStatuses}
        className="w-[170px]"
        aria-label={t.quotes.list.filters.allStatuses}
      />
      {hasActiveFilters && (
        <Button
          variant="text"
          color="primary"
          icon={<LucideX />}
          onPress={handleClearAll}
        >
          {t.quotes.list.filters.clearAll}
        </Button>
      )}
    </>
  );
};
